import type { WorkOrderItem } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import { withChip, withDot } from '#/common/utils/wrappers';
import { color } from '@nuvolo/nuux-theme/utils';
import { CustomChipColors } from '#/common/utils/wrappers/withChip';
import { Key } from '#/types';
import {
  categoriesList,
  impactsList,
  prioritiesList,
  statesList,
  typesList,
  priorities,
} from '../../mappings/mappings';
import { gql } from '@apollo/client';
import { getAssetList, getTenantList, getUserList, getBuildingList } from '#/graphql/queries';

// Priority dot colors
export const priorityColorMap: Record<string, string> = {
  very_low: color('success', 'light3'), // Very low
  low: color('success', 'light'), // Low
  medium: color('reporting', 'green'), // Medium - actually yellow
  high: color('warn', 'light'), // High
  critical: color('error', 'light'), // Critical
};

// Chip colors keyed by lowercase raw DB value (covers both TMM and Salesforce variants).
export const workOrderStateColors: Record<string, CustomChipColors> = {
  // TMM native
  open: { backgroundColor: color('info', 'dark', 0.15), textColor: color('info', 'dark') },
  in_progress: { backgroundColor: color('warn', 'dark', 0.15), textColor: color('warn', 'dark') },
  pending_review: {
    backgroundColor: color('error', 'dark', 0.15),
    textColor: color('error', 'dark'),
  },
  closed: { backgroundColor: color('success', 'dark', 0.15), textColor: color('success', 'dark') },
  canceled: { backgroundColor: color('ui', 'dark', 0.1), textColor: color('ui', 'dark') },
  on_hold: { backgroundColor: color('ui', 'dark', 0.1), textColor: color('ui', 'dark') },
  // Salesforce raw values
  new: { backgroundColor: color('info', 'dark', 0.15), textColor: color('info', 'dark') },
  completed: {
    backgroundColor: color('success', 'dark', 0.15),
    textColor: color('success', 'dark'),
  },
  'in progress': { backgroundColor: color('warn', 'dark', 0.15), textColor: color('warn', 'dark') },
  'on hold': { backgroundColor: color('ui', 'dark', 0.1), textColor: color('ui', 'dark') },
};

export const hourLabel = (value: number) => `${value} ${value === 1 ? 'hour' : 'hours'}`;

const columns: ColumnProps<WorkOrderItem, Key<WorkOrderItem>>[] = [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'displayNumber',
    caption: 'Number',
    visible: true,
    fixed: true,
    openOnClick: true,
    dataType: 'string',
    sortIndex: 0,
    sortOrder: 'desc',
  },
  {
    dataField: 'type',
    caption: 'Type',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: typesList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'title',
    caption: 'Title',
    visible: true,
    openOnClick: true,
    dataType: 'string',
  },
  {
    dataField: 'asset.assetTag',
    caption: 'Asset',
    openOnClick: true,
    path: (row) => `/assets/${row.asset?.id}`,
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getAssetList),
      displayExpr: 'assetTag',
    },
  },
  {
    dataField: 'assetId',
    caption: 'AssetId',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'priority',
    caption: 'Priority',
    visible: true,
    dataType: 'number',
    lookup: {
      nullable: true,
      dataSource: prioritiesList,
      displayExpr: 'label',
      valueExpr: 'value',
    },
    cellRender: (e) => {
      if (e.value === null || e.value === undefined) {
        return null;
      }
      // Find the priority based on the numeric value
      const priority = Object.values(priorities).find((p) => p.value === e.value);
      if (!priority) return e.displayValue;

      return withDot(e.displayValue, priorityColorMap[priority.key] || color('ui', 'dark'));
    },
  },
  {
    dataField: 'state',
    caption: 'Status',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: statesList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    cellRender: (e) => {
      if (!e.value) {
        return null;
      }
      const customColors = workOrderStateColors[e.value as string];
      return withChip(e.displayValue || '', 'normal', customColors);
    },
  },
  {
    dataField: 'dueDate',
    caption: 'Due Date',
    visible: true,
    dataType: 'date',
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    visible: true,
    allowFiltering: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'startDate',
    caption: 'Start Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'endDate',
    caption: 'End Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'duration',
    caption: 'Duration',
    visible: false,
    dataType: 'string',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
  },
  {
    dataField: 'plannedStartDate',
    caption: 'Planned Start Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'plannedEndDate',
    caption: 'Planned End Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'assignmentGroupId',
    caption: 'Assignment Group ID',
    visible: false,
    dataType: 'uuid',
  },
  {
    dataField: 'assignedToId',
    caption: 'Assigned To ID',
    visible: false,
    dataType: 'uuid',
  },
  {
    dataField: 'assignedTo.name',
    caption: 'Assigned To',
    visible: false,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'businessDownTime',
    caption: 'Business Down Time',
    visible: false,
    showInColumnChooser: false,
    dataType: 'number',
  },
  {
    dataField: 'downTimeStart',
    caption: 'Down Time Start',
    visible: false,
    showInColumnChooser: false,
    dataType: 'datetime',
  },
  {
    dataField: 'downTimeEnd',
    caption: 'Down Time End',
    visible: false,
    showInColumnChooser: false,
    dataType: 'datetime',
  },
  {
    dataField: 'category',
    caption: 'Category',
    visible: false,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: categoriesList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'checklist',
    caption: 'Checklist',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'closedNotes',
    caption: 'Closed Notes',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'closedAt',
    caption: 'Closed At',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'closedBy.name',
    caption: 'Closed By',
    visible: false,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'notes',
    caption: 'Notes',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'description',
    caption: 'Description',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'estimatedCostValue',
    caption: 'Estimated Cost',
    visible: false,
    dataType: 'number',
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
  },
  {
    dataField: 'actualCostValue',
    caption: 'Actual Cost',
    visible: false,
    dataType: 'number',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
  },
  {
    dataField: 'estimatedEffort',
    caption: 'Estimated Effort',
    visible: false,
    dataType: 'number',
    calcDisplayValue: (row) =>
      !!row.estimatedEffort && row.estimatedEffort > 0 ? hourLabel(row.estimatedEffort) : '',
  },
  {
    dataField: 'actualEffort',
    caption: 'Actual Effort',
    visible: false,
    dataType: 'number',
    calcDisplayValue: (row) =>
      !!row.actualEffort && row.actualEffort > 0 ? hourLabel(row.actualEffort) : '',
  },
  {
    dataField: 'expectedOperationalDate',
    caption: 'Expected Operational Date',
    visible: false,
    dataType: 'date',
  },
  {
    dataField: 'impact',
    caption: 'Impact',
    visible: false,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: impactsList,
      displayExpr: 'label',
      valueExpr: 'value',
    },
  },
  {
    dataField: 'isArchived',
    caption: 'Archived',
    dataType: 'boolean',
  },
  {
    dataField: 'seenByAssignee',
    caption: 'Seen By Assignee',
    dataType: 'boolean',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'assignmentDateTime',
    caption: 'Assignment Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'seenDateTime',
    caption: 'Seen Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'startOfWorkDateTime',
    caption: 'Start Of Work Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'endOfWorkDateTime',
    caption: 'End Of Work Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'assetLocation.id',
    caption: 'Location',
    visible: true,
    dataType: 'string',
    filterField: 'assetLocation.name',
    headerFilter: {
      getItemList: gql(getBuildingList),
      displayExpr: 'name',
      valueExpr: 'id',
    },
    cellRender: (e) => e.data?.assetLocation?.name || '', // for display
  },
];

export default columns;
